import { useEffect, useState, type ReactNode } from 'react'
import { shopifyComponentCopy } from '@testfeed/product-language'
import { Progress } from '../art/Art'
import { HomeArt } from '../art/HomeArt'
import { PrivateByDesign } from './Small'
import { OnboardingCallStep } from './OnboardingCall'
import {
  canResync,
  connectedStoreBody,
  currentStep,
  hasPlan,
  setupProgress,
  stepStatus,
  syncReadyBody,
  STEP_ORDER,
  type StepId,
  type StepStatus,
} from '../derive'
import type { HomeData } from '../types'
import type { Busy, HomeActions } from '../useHome'
import { useAppBridge } from '@shopify/app-bridge-react'
import { trackShopifyAction } from '../../../shopifyUiTracking'

export const GUIDE_ID = 'setup-guide'

interface Props {
  data: HomeData
  busy: Busy
  actions: HomeActions
  /** Bumped by other parts of the page to open the guide on the current step. */
  focusSignal: number
}

function StatusIcon({ status }: { status: StepStatus }) {
  switch (status) {
    case 'done':
      return <s-icon type="check-circle" tone="success"></s-icon>
    case 'problem':
      return <s-icon type="alert-circle" tone="critical"></s-icon>
    case 'working':
      return <s-spinner size="base" accessibilityLabel="In progress"></s-spinner>
    case 'locked':
      return <s-icon type="lock" tone="neutral"></s-icon>
    default:
      return <s-icon type="circle-dashed" tone="neutral"></s-icon>
  }
}

/** Setup guide composition from Shopify's homepage template. Steps finish themselves; merchants don't tick them. */
export function SetupGuide({ data, busy, actions, focusSignal }: Props) {
  const shopify = useAppBridge()
  const copy = shopifyComponentCopy.appHome
  const TITLES: Record<StepId, string> = {
    connect: copy.stepConnectTitle,
    plan: copy.stepPlanTitle,
    sync: copy.stepSyncTitle,
  }
  const STATUS_WORD: Record<StepStatus, string> = {
    todo: copy.statusWordTodo,
    locked: copy.statusWordLocked,
    working: copy.statusWordWorking,
    problem: copy.statusWordProblem,
    done: copy.statusWordDone,
  }
  const progress = setupProgress(data)
  const collapsed = data.prefs.guideCollapsed
  const [open, setOpen] = useState<StepId | 'meeting' | null>(currentStep(data))

  // When a step finishes, move on to the next one.
  const current = currentStep(data)
  useEffect(() => setOpen(current), [current])

  useEffect(() => {
    if (!focusSignal) return
    if (collapsed) actions.savePrefs({ guideCollapsed: false })
    setOpen(current)
    document.getElementById(GUIDE_ID)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusSignal])

  return (
    <s-section id={GUIDE_ID} accessibilityLabel={copy.guideAriaLabel}>
      <s-grid gap="small">
        <s-grid gap="small-200">
          <s-grid gridTemplateColumns="1fr auto auto" gap="small-300" alignItems="center">
            <s-heading>{copy.guideHeading}</s-heading>
            <s-button
              accessibilityLabel={copy.guideDismissAction}
              variant="tertiary"
              tone="neutral"
              icon="x"
              onClick={() => actions.savePrefs({ guideDismissed: true })}
            ></s-button>
            <s-button
              accessibilityLabel={collapsed ? copy.guideExpandAction : copy.guideCollapseAction}
              variant="tertiary"
              tone="neutral"
              icon={collapsed ? 'chevron-down' : 'chevron-up'}
              onClick={() => actions.savePrefs({ guideCollapsed: !collapsed })}
            ></s-button>
          </s-grid>
          <s-paragraph color="subdued">
            {progress.complete
              ? copy.guideProgressAllDone.replace('{total}', String(progress.total))
              : copy.guideProgressPartial
                  .replace('{done}', String(progress.done))
                  .replace('{total}', String(progress.total))}
          </s-paragraph>
          <Progress value={(progress.done / progress.total) * 100} label="Setup progress" />
        </s-grid>

        {progress.complete && (
          <s-box padding="base" background="subdued" borderRadius="base">
            <s-grid gridTemplateColumns="auto 1fr auto" gap="small" alignItems="center">
              <s-icon type="check-circle" tone="success"></s-icon>
              <s-text>{copy.guideAllDoneBody}</s-text>
              <s-button onClick={() => actions.savePrefs({ guideDismissed: true })}>{copy.guideHideAction}</s-button>
            </s-grid>
          </s-box>
        )}

        <s-box borderRadius="base" border="base" background="base" display={collapsed ? 'none' : 'auto'}>
          {STEP_ORDER.map((id, i) => {
            const status = stepStatus(data, id)
            const isOpen = open === id
            return (
              <s-box key={id}>
                {i > 0 && <s-divider></s-divider>}
                <s-clickable
                  padding="small"
                  onClick={() => trackShopifyAction(shopify, 'toggle-guide-step', () => setOpen(isOpen ? null : id))}
                  accessibilityLabel={`${TITLES[id]}. ${STATUS_WORD[status]}. ${isOpen ? 'Hide' : 'Show'} details`}
                >
                  <s-grid gridTemplateColumns="auto 1fr auto" gap="small" alignItems="center">
                    <StatusIcon status={status} />
                    <s-text
                      type={status === 'done' ? 'generic' : 'strong'}
                      color={status === 'done' ? 'subdued' : 'base'}
                    >
                      {TITLES[id]}
                    </s-text>
                    <s-icon type={isOpen ? 'chevron-up' : 'chevron-down'} tone="neutral"></s-icon>
                  </s-grid>
                </s-clickable>
                <s-box padding="small" paddingBlockStart="none" display={isOpen ? 'auto' : 'none'}>
                  <s-box padding="base" background="subdued" borderRadius="base">
                    <s-grid
                      gridTemplateColumns="@container (inline-size <= 420px) 1fr, 1fr auto"
                      gap="base"
                      alignItems="center"
                    >
                      <s-grid gap="small-200">
                        <StepBody
                          id={id}
                          status={status}
                          data={data}
                          busy={busy}
                          actions={actions}
                          isCurrent={current === id}
                        />
                      </s-grid>
                      <s-box display="@container (inline-size <= 420px) none, auto">
                        {isOpen && (
                          <HomeArt name={id} alt={`${TITLES[id]} example`} size={96} background="transparent" />
                        )}
                      </s-box>
                    </s-grid>
                  </s-box>
                </s-box>
              </s-box>
            )
          })}
          <s-divider></s-divider>
          <OnboardingCallStep
            shopify={shopify}
            open={open === 'meeting'}
            onToggle={() => setOpen(open === 'meeting' ? null : 'meeting')}
          />
          <s-divider></s-divider>
          <s-box padding="small">
            <PrivateByDesign go={actions.go} framed />
          </s-box>
        </s-box>
      </s-grid>
    </s-section>
  )
}

function Actions({ children }: { children: ReactNode }) {
  return (
    <s-stack direction="inline" gap="small-200" alignItems="center">
      {children}
    </s-stack>
  )
}

function StepBody({
  id,
  status,
  data,
  busy,
  actions,
  isCurrent,
}: {
  id: StepId
  status: StepStatus
  data: HomeData
  busy: Busy
  actions: HomeActions
  isCurrent: boolean
}) {
  const copy = shopifyComponentCopy.appHome
  const variant = isCurrent ? 'primary' : 'secondary'

  if (id === 'connect') {
    if (data.connect.status === 'granted') {
      return <s-paragraph>{connectedStoreBody(data.connect)}</s-paragraph>
    }
    return (
      <>
        {status === 'problem' ? (
          <s-paragraph tone="critical">{copy.connectProblemBody}</s-paragraph>
        ) : (
          <s-paragraph>{copy.connectDefaultBody}</s-paragraph>
        )}
        <Actions>
          <s-button variant={variant} loading={busy === 'connect'} onClick={actions.connect}>
            {status === 'problem' ? copy.connectTryAgainAction : copy.connectAction}
          </s-button>
          <s-button variant="tertiary" tone="neutral" onClick={() => actions.go('/app/privacy')}>
            {copy.connectWhatWeReadAction}
          </s-button>
        </Actions>
      </>
    )
  }

  if (id === 'plan') {
    if (hasPlan(data.plan)) {
      const p = data.plan as Extract<HomeData['plan'], { status: 'active' | 'trial' }>
      return (
        <s-paragraph>
          {copy.planActiveBody.replace('{name}', p.name).replace('{testsIncluded}', String(p.testsIncluded))}
          {p.status === 'trial' && copy.planTrialSuffix.replace('{trialDaysLeft}', String(p.trialDaysLeft))}
        </s-paragraph>
      )
    }
    return (
      <>
        <s-paragraph tone={status === 'problem' ? 'critical' : 'auto'}>
          {data.plan.status === 'cancelled'
            ? copy.planCancelledBody.replace('{previousPlan}', data.plan.previousPlan)
            : copy.planDefaultBody}
        </s-paragraph>
        <Actions>
          <s-button variant={variant} onClick={actions.choosePlan}>
            {copy.planChooseAction}
          </s-button>
        </Actions>
      </>
    )
  }

  // sync
  const s = data.sync
  if (s.status === 'done') {
    return (
      <>
        <s-paragraph>{syncReadyBody(s)}</s-paragraph>
        <Actions>
          <s-button icon="refresh" disabled={!canResync(data)} loading={busy === 'resync'} onClick={actions.resync}>
            {s.refresh?.status === 'running' ? copy.resyncingAction : copy.resyncAction}
          </s-button>
        </Actions>
      </>
    )
  }
  if (s.status === 'syncing') {
    let progress = null
    let body: string = copy.syncingNoProgressBody
    if (s.progress !== null) {
      progress = <Progress value={s.progress} label={copy.syncProgressLabel} />
      body = copy.syncingBody.replace('{percent}', String(Math.round(s.progress)))
    }
    return (
      <>
        {progress}
        <s-paragraph color="subdued">{body}</s-paragraph>
      </>
    )
  }
  if (status === 'locked') {
    const waitingOn = data.connect.status !== 'granted' ? copy.syncWaitingOnConnect : copy.syncWaitingOnPlan
    return (
      <>
        <s-paragraph>{copy.syncLockedBody.replace('{waitingOn}', waitingOn)}</s-paragraph>
        <Actions>
          <s-button disabled>{copy.syncStartAction}</s-button>
        </Actions>
      </>
    )
  }
  return (
    <>
      {s.status === 'failed' ? (
        <s-paragraph tone="critical">{s.reason}</s-paragraph>
      ) : (
        <s-paragraph>{copy.syncDefaultBody}</s-paragraph>
      )}
      <Actions>
        <s-button variant={variant} loading={busy === 'sync'} onClick={actions.startSync}>
          {s.status === 'failed' ? copy.syncRetryAction : copy.syncStartAction}
        </s-button>
      </Actions>
    </>
  )
}
